import { z } from "zod";

/**
 * Environment Variables Validation
 * Validates all environment variables on application startup
 * Prevents runtime failures due to missing or invalid env vars
 */

// Define the schema for environment variables
const envSchema = z.object({
  // Cloudflare API Configuration
  VITE_CLOUDFLARE_API_URL: z
    .string()
    .url("VITE_CLOUDFLARE_API_URL must be a valid URL")
    .refine(
      (url) => {
        try {
          const parsed = new URL(url);
          return ["http:", "https:"].includes(parsed.protocol);
        } catch {
          return false;
        }
      },
      { message: "VITE_CLOUDFLARE_API_URL must use HTTP or HTTPS protocol" }
    )
    .default("https://reno-quoting-pro-api.renowarriors.workers.dev"),

  // R2 Storage Configuration (Optional)
  VITE_R2_PUBLIC_URL: z
    .string()
    .url("VITE_R2_PUBLIC_URL must be a valid URL")
    .optional()
    .or(z.literal("")),

  // Application Environment
  VITE_APP_ENV: z.enum(["development", "production", "test"]).default("development"),

  // Development Mode Detection
  DEV: z.boolean().optional(),

  // Vite-specific vars (optional but good to validate)
  MODE: z.string().optional(),
  PROD: z.boolean().optional(),
});

// Infer TypeScript types from schema
export type Env = z.infer<typeof envSchema>;

// Parse and validate environment variables
function parseEnv(): Env {
  try {
    return envSchema.parse(import.meta.env);
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errorMessages = error.errors
        .map((err) => `${err.path.join(".")}: ${err.message}`)
        .join("\n");

      throw new Error(`Environment validation failed:\n${errorMessages}`);
    }
    throw error;
  }
}

// Export validated environment variables
export const env = parseEnv();

// Helper function to check if we're in production
export const isProduction = () => env.VITE_APP_ENV === "production";

// Helper function to check if we're in development
export const isDevelopment = () => env.VITE_APP_ENV === "development";

// Helper function to get API base URL with validation
export const getApiBaseUrl = (): string => {
  if (!env.VITE_CLOUDFLARE_API_URL) {
    throw new Error("API URL is not configured");
  }
  return env.VITE_CLOUDFLARE_API_URL;
};

// Helper function to get R2 public URL with validation
export const getR2PublicUrl = (): string => {
  return env.VITE_R2_PUBLIC_URL || "";
};

// Development-only logging helper
export const devLog = (...args: any[]) => {
  if (isDevelopment()) {
    console.log("[DEV]", ...args);
  }
};

// Error logging helper that respects environment
export const logError = (message: string, error?: Error) => {
  if (isDevelopment()) {
    console.error(`[ERROR] ${message}`, error);
  } else {
    // In production, you might want to send to a logging service
    // For now, we'll just log the message without sensitive details
    console.error(`[ERROR] ${message}`);
  }
};
